import { useEffect } from "react";
import { PDF_TEXT_REDACTIONS, type RedactionEntry } from "./pdfTextRedactions";
import type { PdfViewerInstance } from "./use-pdf-viewer";

/**
 * Covers text-layer spans whose textContent matches any entry in
 * PDF_TEXT_REDACTIONS with an opaque white background so the canvas-rendered
 * text beneath is visually hidden. `display: none` doesn't work because the
 * visual text lives on the canvas — the text layer is just a transparent
 * overlay for selection. Re-runs on every `textlayerrendered` event so it
 * survives zoom, scroll, and page-fit changes.
 */
export function usePdfTextRedaction(
  instanceRef: React.RefObject<PdfViewerInstance | null>,
  containerRef: React.RefObject<HTMLDivElement | null>,
  isReady: boolean,
) {
  useEffect(() => {
    if (!isReady || PDF_TEXT_REDACTIONS.length === 0) return;
    const instance = instanceRef.current;
    const container = containerRef.current;
    if (!instance || !container) return;

    const applyRedactions = () => {
      const spans = container.querySelectorAll<HTMLSpanElement>(
        ".textLayer span",
      );
      for (const span of spans) {
        if (shouldRedact(span.textContent ?? "", PDF_TEXT_REDACTIONS)) {
          // Paint over the canvas text beneath with opaque white.
          span.style.background = "white";
          span.style.color = "transparent";
          span.style.borderRadius = "0";
          span.dataset.redacted = "true";
        }
      }
    };

    // Apply immediately for already-rendered pages.
    applyRedactions();

    // Re-apply whenever a text layer finishes rendering.
    let rafId = 0;
    const onTextLayerRendered = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(applyRedactions);
    };
    instance.eventBus.on("textlayerrendered", onTextLayerRendered);

    return () => {
      cancelAnimationFrame(rafId);
      instance.eventBus.off("textlayerrendered", onTextLayerRendered);
    };
  }, [isReady, instanceRef, containerRef]);
}

function shouldRedact(content: string, entries: RedactionEntry[]): boolean {
  const trimmed = content.trim();
  if (trimmed.length === 0) return false;

  for (const entry of entries) {
    const mode = entry.mode ?? "exact";
    switch (mode) {
      case "exact":
        if (trimmed === entry.text) return true;
        break;
      case "includes":
        if (trimmed.includes(entry.text)) return true;
        break;
      case "startsWith":
        if (trimmed.startsWith(entry.text)) return true;
        break;
    }
  }
  return false;
}
